import { msg } from '@lingui/core/macro';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, type LayoutRow, getMyLayout, putMyLayout } from '../lib/api';
import { useToast } from '../components/Toast';

export const LAYOUT_SAVE_TOAST = msg`Could not save sidebar order. Please try again.`;

const PUT_DEBOUNCE_MS = 200;

export interface UserLayout {
  /** Map keyed by channel_id; an absent key falls back to 作者顺序 (rule ②). */
  byChannel: Map<string, LayoutRow>;
  loaded: boolean;
}

export function useUserLayout() {
  const { showToast } = useToast();
  const [layout, setLayout] = useState<UserLayout>(() => ({
    byChannel: new Map(),
    loaded: false,
  }));
  // Last server-confirmed snapshot for rollback on PUT failure.
  const confirmedRef = useRef<Map<string, LayoutRow>>(new Map());
  // Pending dirty rows queued for next PUT.
  const dirtyRef = useRef<Map<string, LayoutRow>>(new Map());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { layout: rows } = await getMyLayout();
        if (cancelled) return;
        const m = new Map<string, LayoutRow>();
        for (const r of rows) m.set(r.channel_id, r);
        confirmedRef.current = new Map(m);
        setLayout({ byChannel: m, loaded: true });
      } catch (err) {
        // 401 / network: no toast; initial load silently falls back to 作者侧顺序.
        if (cancelled) return;
        setLayout({ byChannel: new Map(), loaded: true });
      }
    })();
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const flushDirty = useCallback(async () => {
    const dirty = Array.from(dirtyRef.current.values());
    if (dirty.length === 0) return;
    dirtyRef.current = new Map();
    try {
      await putMyLayout(dirty);
      // Persist to confirmed snapshot.
      for (const r of dirty) confirmedRef.current.set(r.channel_id, r);
    } catch (err) {
      const _ = err instanceof ApiError ? err.status : 0;
      showToast(LAYOUT_SAVE_TOAST);
      // Rollback dirty rows to confirmed snapshot.
      setLayout((prev) => {
        const next = new Map(prev.byChannel);
        for (const r of dirty) {
          const conf = confirmedRef.current.get(r.channel_id);
          if (conf) next.set(r.channel_id, conf);
          else next.delete(r.channel_id);
        }
        return { byChannel: next, loaded: prev.loaded };
      });
    }
  }, [showToast]);

  const queuePut = useCallback(
    (rows: LayoutRow[]) => {
      for (const r of rows) dirtyRef.current.set(r.channel_id, r);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void flushDirty();
      }, PUT_DEBOUNCE_MS);
    },
    [flushDirty],
  );

  /**
   * pinChannel — layout rule ③ + content-lock rule ③: position = MIN(已有 position) - 1.0,
   * using a smaller numeric position each time. This moves the channel to the front of the current layout.
   * Multiple pins are allowed (#366 rule ③ "个人 pin 数量不限"). Constraint: do not split
   * ordering into a second pinned BOOL source (reverse grep `pinned\s+BOOL` 0 hit).
   */
  const pinChannel = useCallback(
    (channelId: string) => {
      setLayout((prev) => {
        const next = new Map(prev.byChannel);
        let minPos = 0;
        for (const r of next.values()) {
          if (r.position < minPos) minPos = r.position;
        }
        const newPos = minPos - 1.0;
        const existing = next.get(channelId);
        const row: LayoutRow = {
          channel_id: channelId,
          collapsed: existing?.collapsed ?? 0,
          position: newPos,
        };
        next.set(channelId, row);
        queuePut([row]);
        return { byChannel: next, loaded: prev.loaded };
      });
    },
    [queuePut],
  );

  /**
   * unpinChannel — reverse pin: reset position to current MAX + 1.0 (move to the end,
   * 作者侧 fallback applies again). The "取消置顶" label must match content-lock rule ③ exactly.
   */
  const unpinChannel = useCallback(
    (channelId: string) => {
      setLayout((prev) => {
        const next = new Map(prev.byChannel);
        let maxPos = 0;
        for (const r of next.values()) {
          if (r.position > maxPos) maxPos = r.position;
        }
        const newPos = maxPos + 1.0;
        const existing = next.get(channelId);
        const row: LayoutRow = {
          channel_id: channelId,
          collapsed: existing?.collapsed ?? 0,
          position: newPos,
        };
        next.set(channelId, row);
        queuePut([row]);
        return { byChannel: next, loaded: prev.loaded };
      });
    },
    [queuePut],
  );

  const isPinned = useCallback(
    (channelId: string): boolean => {
      const row = layout.byChannel.get(channelId);
      return row != null && row.position < 0;
    },
    [layout],
  );

  return { layout, pinChannel, unpinChannel, isPinned };
}
